'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BusinessLoanCalculator() {
  const t = useTranslations("tools.businessLoanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    loanAmount: 10000,
    interestRate: 10,
    compoundFreq: 12,
    termYears: 5,
    termMonths: 0,
    paybackFreq: 12,
    originationFee: 5,
    documentationFee: 750,
    otherFees: 0,
  });

  const [results, setResults] = useState<any>(null);
  const [showAmortization, setShowAmortization] = useState(false);
  const [amortizationSchedule, setAmortizationSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueLight: '#eff6ff',
    green: '#8bbc21',
    red: '#dc2626',
  };

  const calculateAPR = (principal: number, payment: number, numPayments: number, paymentsPerYear: number): number => {
    let apr = 0.1;
    const tolerance = 0.00001;
    const maxIterations = 100;

    for (let i = 0; i < maxIterations; i++) {
      const periodicRate = apr / paymentsPerYear;
      const pv = payment * ((1 - Math.pow(1 + periodicRate, -numPayments)) / periodicRate);
      const diff = pv - principal;

      if (Math.abs(diff) < tolerance) {
        return apr * 100;
      }

      const derivative = -payment * numPayments * Math.pow(1 + periodicRate, -numPayments - 1) / paymentsPerYear;
      apr = apr - diff / derivative;
    }

    return apr * 100;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { loanAmount, interestRate, compoundFreq, termYears, termMonths, paybackFreq, originationFee, documentationFee, otherFees } = inputs;

    const totalMonths = termYears * 12 + termMonths;
    const totalPayments = Math.round((totalMonths / 12) * paybackFreq);

    const periodicRate = (interestRate / 100) / compoundFreq;
    const paymentsPerYear = paybackFreq;
    const effectiveRate = Math.pow(1 + periodicRate, compoundFreq / paymentsPerYear) - 1;

    const paymentAmount = loanAmount * (effectiveRate * Math.pow(1 + effectiveRate, totalPayments)) / (Math.pow(1 + effectiveRate, totalPayments) - 1);

    const totalPaid = paymentAmount * totalPayments;
    const totalInterest = totalPaid - loanAmount;

    const totalFees = loanAmount * (originationFee / 100) + documentationFee + otherFees;
    const interestPlusFees = totalInterest + totalFees;

    const actualLoanReceived = loanAmount - totalFees;
    const realAPR = calculateAPR(actualLoanReceived, paymentAmount, totalPayments, paymentsPerYear);

    // Generate amortization schedule
    let balance = loanAmount;
    const schedule = [];
    for (let i = 1; i <= totalPayments; i++) {
      const interestPayment = balance * effectiveRate;
      const principalPayment = paymentAmount - interestPayment;
      balance -= principalPayment;

      if (balance < 0) balance = 0;

      schedule.push({
        payment: i,
        paymentAmount: paymentAmount,
        principalPayment: principalPayment,
        interestPayment: interestPayment,
        balance: balance,
      });
    }

    setAmortizationSchedule(schedule);
    setResults({
      paymentAmount,
      totalPaid,
      totalInterest,
      totalFees,
      interestPlusFees,
      realAPR,
      totalPayments,
      loanAmount,
    });
  };

  const paybackFreqText: { [key: number]: string } = {
    365: t('frequencyDay'),
    52: t('frequencyWeek'),
    26: t('frequency2Weeks'),
    24: t('frequencyHalfMonth'),
    12: t('frequencyMonth'),
    4: t('frequencyQuarter'),
    2: t('frequency6Months'),
    1: t('frequencyYear'),
  };

  const compoundFreqOptions = [
    { value: 12, label: t('compoundMonthly') },
    { value: 1, label: t('compoundAnnually') },
    { value: 2, label: t('compoundSemiAnnually') },
    { value: 4, label: t('compoundQuarterly') },
    { value: 24, label: t('compoundSemiMonthly') },
    { value: 26, label: t('compoundBiweekly') },
    { value: 52, label: t('compoundWeekly') },
    { value: 365, label: t('compoundDaily') },
  ];

  const paybackFreqOptions = [
    { value: 12, label: t('paybackMonthly') },
    { value: 365, label: t('paybackDaily') },
    { value: 52, label: t('paybackWeekly') },
    { value: 26, label: t('paybackBiweekly') },
    { value: 24, label: t('paybackSemiMonthly') },
    { value: 4, label: t('paybackQuarterly') },
    { value: 2, label: t('paybackSemiAnnually') },
    { value: 1, label: t('paybackAnnually') },
  ];

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorLayout: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', alignItems: 'start' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGroup: getResponsiveStyle(
      {},
      { marginBottom: '1.25rem' },
      { marginBottom: '1.5rem' },
      { marginBottom: '1.5rem' }
    ),
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      background: 'white',
    },
    inputWithPrefix: {
      position: 'relative' as const,
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    inputSuffix: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    termInputs: getResponsiveStyle(
      { display: 'grid', gap: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: `linear-gradient(135deg, ${colors.blue} 0%, #1d4ed8 100%)`,
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1.1rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem', position: 'sticky' as const, top: '2rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      marginBottom: '0.75rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #e2e8f0',
    },
    resultItemHighlight: {
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      borderLeft: `4px solid ${colors.blue}`,
    },
    resultLabel: getResponsiveStyle(
      { fontWeight: 600, color: '#475569' },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    resultValueHighlight: getResponsiveStyle(
      { fontWeight: 700, color: colors.blue },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.3rem' }
    ),
    chartContainer: {
      marginTop: '2rem',
      textAlign: 'center' as const,
    },
    chartLegend: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1.5rem',
      flexWrap: 'wrap' as const,
      marginTop: '1rem',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
    },
    legendColor: {
      width: '16px',
      height: '16px',
      borderRadius: '3px',
    },
    toggleBtn: {
      background: '#f1f5f9',
      color: '#475569',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      marginTop: '1.5rem',
    },
    amortizationTable: {
      marginTop: '1.5rem',
      overflowX: 'auto' as const,
      maxHeight: '400px',
      overflowY: 'auto' as const,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '0.9rem',
    },
    th: {
      background: '#f1f5f9',
      padding: '0.75rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#334155',
      position: 'sticky' as const,
      top: 0,
    },
    td: {
      padding: '0.75rem',
      borderBottom: '1px solid #e2e8f0',
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    refList: {
      listStyle: 'none',
      padding: 0,
    },
    refItem: getResponsiveStyle(
      { marginBottom: '0.75rem', lineHeight: 1.6 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const renderPieChart = () => {
    if (!results) return null;

    const total = results.loanAmount + results.totalInterest + results.totalFees;
    const principalPct = (results.loanAmount / total) * 100;
    const interestPct = (results.totalInterest / total) * 100;
    const feesPct = (results.totalFees / total) * 100;

    const cx = 125;
    const cy = 125;
    const radius = 80;

    let currentAngle = -90;

    const slices = [
      { percent: principalPct, color: colors.blue, label: t('chartPrincipal') },
      { percent: interestPct, color: colors.green, label: t('chartInterest') },
      { percent: feesPct, color: colors.red, label: t('chartFees') },
    ];

    return (
      <svg width="250" height="250" style={{ margin: '1.5rem auto', display: 'block' }}>
        {slices.map((slice, index) => {
          if (slice.percent <= 0) return null;

          const sliceAngle = (slice.percent / 100) * 360;
          const endAngle = currentAngle + sliceAngle;

          const x1 = cx + radius * Math.cos(currentAngle * Math.PI / 180);
          const y1 = cy + radius * Math.sin(currentAngle * Math.PI / 180);
          const x2 = cx + radius * Math.cos(endAngle * Math.PI / 180);
          const y2 = cy + radius * Math.sin(endAngle * Math.PI / 180);

          const largeArc = sliceAngle > 180 ? 1 : 0;

          const pathD = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

          const labelAngle = currentAngle + sliceAngle / 2;
          const labelRadius = radius * 0.65;
          const labelX = cx + labelRadius * Math.cos(labelAngle * Math.PI / 180);
          const labelY = cy + labelRadius * Math.sin(labelAngle * Math.PI / 180);

          currentAngle = endAngle;

          return (
            <g key={index}>
              <path
                d={pathD}
                fill={slice.color}
                stroke="white"
                strokeWidth="2"
              >
                <title>{`${slice.label}: ${slice.percent.toFixed(1)}%`}</title>
              </path>
              {slice.percent > 5 && (
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontWeight="bold"
                  fontSize="16"
                >
                  {slice.percent.toFixed(0)}%
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.h2}>💼 {t('toolTitle')}</h2>
        </div>

        <div style={styles.calculatorLayout}>
          <div style={styles.calculatorForm}>
            <form onSubmit={handleCalculate}>
              <div style={styles.formGroup}>
                <label htmlFor="loan-amount" style={styles.label}>{t('loanAmountLabel')}</label>
                <div style={styles.inputWithPrefix}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="number"
                    id="loan-amount"
                    value={inputs.loanAmount}
                    onChange={(e) => setInputs({...inputs, loanAmount: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="100"
                    required
                    style={{...styles.input, paddingLeft: '2rem'}}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="interest-rate" style={styles.label}>{t('interestRateLabel')}</label>
                <div style={styles.inputWithPrefix}>
                  <input
                    type="number"
                    id="interest-rate"
                    value={inputs.interestRate}
                    onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="100"
                    step="0.1"
                    required
                    style={{...styles.input, paddingRight: '2.5rem'}}
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="compound" style={styles.label}>{t('compoundFreqLabel')}</label>
                <select
                  id="compound"
                  value={inputs.compoundFreq}
                  onChange={(e) => setInputs({...inputs, compoundFreq: parseFloat(e.target.value)})}
                  style={styles.select}
                >
                  {compoundFreqOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('loanTermLabel')}</label>
                <div style={styles.termInputs}>
                  <div style={styles.inputWithPrefix}>
                    <input
                      type="number"
                      id="term-years"
                      value={inputs.termYears}
                      onChange={(e) => setInputs({...inputs, termYears: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="50"
                      step="1"
                      style={{...styles.input, paddingRight: '2.5rem'}}
                    />
                    <span style={styles.inputSuffix}>{t('yearsLabel')}</span>
                  </div>
                  <div style={styles.inputWithPrefix}>
                    <input
                      type="number"
                      id="term-months"
                      value={inputs.termMonths}
                      onChange={(e) => setInputs({...inputs, termMonths: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="11"
                      step="1"
                      style={{...styles.input, paddingRight: '2.5rem'}}
                    />
                    <span style={styles.inputSuffix}>{t('monthsLabel')}</span>
                  </div>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="payback" style={styles.label}>{t('paymentFreqLabel')}</label>
                <select
                  id="payback"
                  value={inputs.paybackFreq}
                  onChange={(e) => setInputs({...inputs, paybackFreq: parseFloat(e.target.value)})}
                  style={styles.select}
                >
                  {paybackFreqOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="origination-fee" style={styles.label}>{t('originationFeeLabel')}</label>
                <div style={styles.inputWithPrefix}>
                  <input
                    type="number"
                    id="origination-fee"
                    value={inputs.originationFee}
                    onChange={(e) => setInputs({...inputs, originationFee: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="100"
                    step="0.1"
                    style={{...styles.input, paddingRight: '2.5rem'}}
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="documentation-fee" style={styles.label}>{t('documentationFeeLabel')}</label>
                <div style={styles.inputWithPrefix}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="number"
                    id="documentation-fee"
                    value={inputs.documentationFee}
                    onChange={(e) => setInputs({...inputs, documentationFee: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="10"
                    style={{...styles.input, paddingLeft: '2rem'}}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="other-fees" style={styles.label}>{t('otherFeesLabel')}</label>
                <div style={styles.inputWithPrefix}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="number"
                    id="other-fees"
                    value={inputs.otherFees}
                    onChange={(e) => setInputs({...inputs, otherFees: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="10"
                    style={{...styles.input, paddingLeft: '2rem'}}
                  />
                </div>
              </div>

              <button
                type="submit"
                style={styles.calculateButton}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('calculateButton')}
              </button>
            </form>
          </div>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.h2}>📊 {t('resultsTitle')}</h3>

              <div style={{...styles.resultItem, ...styles.resultItemHighlight}}>
                <span style={styles.resultLabel}>{t('paymentAmountLabel')}</span>
                <span style={styles.resultValueHighlight}>
                  ${results.paymentAmount.toFixed(2)} {t('per')} {paybackFreqText[inputs.paybackFreq]}
                </span>
              </div>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('totalPaymentsLabel')}</span>
                <span style={styles.resultValue}>
                  ${results.totalPaid.toFixed(2)} ({results.totalPayments} {t('payments')})
                </span>
              </div>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('totalInterestLabel')}</span>
                <span style={styles.resultValue}>${results.totalInterest.toFixed(2)}</span>
              </div>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('interestFeesLabel')}</span>
                <span style={styles.resultValue}>${results.interestPlusFees.toFixed(2)}</span>
              </div>

              <div style={{...styles.resultItem, ...styles.resultItemHighlight}}>
                <span style={styles.resultLabel}>{t('realAprLabel')}</span>
                <span style={styles.resultValueHighlight}>{results.realAPR.toFixed(3)}%</span>
              </div>

              <div style={styles.chartContainer}>
                {renderPieChart()}
                <div style={styles.chartLegend}>
                  <div style={styles.legendItem}>
                    <div style={{...styles.legendColor, background: colors.blue}}></div>
                    <span>{t('chartPrincipal')}</span>
                  </div>
                  <div style={styles.legendItem}>
                    <div style={{...styles.legendColor, background: colors.green}}></div>
                    <span>{t('chartInterest')}</span>
                  </div>
                  <div style={styles.legendItem}>
                    <div style={{...styles.legendColor, background: colors.red}}></div>
                    <span>{t('chartFees')}</span>
                  </div>
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <button
                  style={styles.toggleBtn}
                  onClick={() => setShowAmortization(!showAmortization)}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                >
                  {showAmortization ? t('hideAmortization') : t('viewAmortization')}
                </button>
              </div>

              {showAmortization && (
                <div style={styles.amortizationTable}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>{t('paymentNum')}</th>
                        <th style={styles.th}>{t('payment')}</th>
                        <th style={styles.th}>{t('principal')}</th>
                        <th style={styles.th}>{t('interest')}</th>
                        <th style={styles.th}>{t('balance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amortizationSchedule.map((row) => (
                        <tr key={row.payment} style={{ cursor: 'pointer' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <td style={styles.td}>{row.payment}</td>
                          <td style={styles.td}>${row.paymentAmount.toFixed(2)}</td>
                          <td style={styles.td}>${row.principalPayment.toFixed(2)}</td>
                          <td style={styles.td}>${row.interestPayment.toFixed(2)}</td>
                          <td style={styles.td}>${row.balance.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li>{t('howToStep1')}</li>
          <li>{t('howToStep2')}</li>
          <li>{t('howToStep3')}</li>
          <li>{t('howToStep4')}</li>
          <li>{t('howToStep5')}</li>
          <li>{t('howToStep6')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsPara1')}</p>
        <ul style={styles.list}>
          <li><strong>{t('insightAprTitle')}</strong> {t('insightAprText')}</li>
          <li><strong>{t('insightCompareTitle')}</strong> {t('insightCompareText')}</li>
          <li><strong>{t('insightCashFlowTitle')}</strong> {t('insightCashFlowText')}</li>
          <li><strong>{t('insightAmortizationTitle')}</strong> {t('insightAmortizationText')}</li>
          <li><strong>{t('insightLoanTypesTitle')}</strong> {t('insightLoanTypesText')}</li>
        </ul>
        <p style={styles.paragraph}>{t('insightsPara2')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsPara1')}</p>
        <ul style={styles.list}>
          <li><strong>{t('detailTermLoansTitle')}</strong> {t('detailTermLoansText')}</li>
          <li><strong>{t('detailSbaLoansTitle')}</strong> {t('detailSbaLoansText')}</li>
          <li><strong>{t('detailEquipmentTitle')}</strong> {t('detailEquipmentText')}</li>
          <li><strong>{t('detailLocTitle')}</strong> {t('detailLocText')}</li>
          <li><strong>{t('detailInvoiceTitle')}</strong> {t('detailInvoiceText')}</li>
        </ul>
        <p style={styles.paragraph}>{t('detailsPara2')}</p>
        <p style={styles.paragraph}>{t('detailsPara3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ul style={styles.refList}>
          <li style={styles.refItem}>
            <a href="https://www.fblake.bank/calculators/sba-loan-calculator-small-business-loan-calculator/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref1')}
            </a>
          </li>
          <li style={styles.refItem}>
            <a href="https://www.biz2credit.com/business-loan-calculator/why-entrepreneurs-should-use-loan-calculator-before-borrowing" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref2')}
            </a>
          </li>
          <li style={styles.refItem}>
            <a href="https://advancepointcap.com/calculators/business-loan/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref3')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}